import {createContext, useContext, useMemo} from 'react';
import {useSyncExternalStore} from 'use-sync-external-store/shim';

import {
  type AgentChatAvailability,
  wbAgentControllerStore,
} from '../../agentChat/controller';
import {envIsPublicCloud} from '../../config';
import {useOrganizationFlagsQuery} from '../../generated/graphql';
import {useViewer} from '../../state/viewer/hooks';
import {
  isSmartFeatureLocked,
  SMART_FEATURE_OVERRIDES_CONFIG_ID,
  type SmartFeatureOverrides,
} from '../../util/dynamicConfigs/smartFeatureOverrides';
import {jsonParseSafe} from '../../util/json';
import {useStatsigGateWorkspaceAgent} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {useOrganizationDynamicConfigWithLoading} from '../../util/useDynamicConfig';
import {AccountSelectorContext} from '../Search/SearchNav/AccountSelectorContextProvider';
import {AccountType} from '../Search/SearchNav/types';

export type {
  AgentChatAvailability,
  UnavailableReason,
} from '../../agentChat/controller';

/**
 * AgentChatAvailabilityContext supplies authoritative availability inside
 * `AgentChatProvider`; `null` routes reads to the store fallback.
 */
export const AgentChatAvailabilityContext =
  createContext<AgentChatAvailability | null>(null);

/**
 * useAgentChatAvailability returns chat agent availability — the authoritative
 * context value inside `AgentChatProvider`, the store mirror outside it.
 */
export function useAgentChatAvailability(): AgentChatAvailability {
  const fromContext = useContext(AgentChatAvailabilityContext);
  const fromStore = useSyncExternalStore(
    wbAgentControllerStore.subscribe,
    wbAgentControllerStore.getAvailability
  );
  return fromContext ?? fromStore;
}

/**
 * useComputeAgentChatAvailability calculates the agent chat feature
 * availability by checking various feature gates.
 *
 * NOTE: This function is for use by the agent chat provider component; external
 * components should use `useAgentChatAvailability` instead.
 */
export function useComputeAgentChatAvailability(): AgentChatAvailability {
  const {isFeatureGateEnabled, isLoggedIn, isPersonalAccount, orgName, skip} =
    useAgentChatOrgQueryArgs();
  const {enabled: areLlmFeaturesEnabled, loading: isLoading} =
    useLLMFeaturesEnabled(orgName, skip);

  return useMemo(
    () =>
      computeAvailability({
        areLlmFeaturesEnabled,
        isFeatureGateEnabled,
        isLoading,
        isLoggedIn,
        isPersonalAccount,
        isPublicCloud: envIsPublicCloud,
        orgName,
      }),
    [
      areLlmFeaturesEnabled,
      isFeatureGateEnabled,
      isLoading,
      isLoggedIn,
      isPersonalAccount,
      orgName,
    ]
  );
}

/** computeAvailability evaluates whether to enable the chat agent feature. */
export function computeAvailability(inputs: {
  areLlmFeaturesEnabled: boolean;
  isFeatureGateEnabled?: boolean;
  isLoading: boolean;
  isLoggedIn: boolean;
  isPersonalAccount: boolean;
  isPublicCloud: boolean;
  orgName?: string;
}): AgentChatAvailability {
  if (!inputs.isLoggedIn) {
    return {isAvailable: false, unavailableReason: 'logged-out'};
  }

  if (!inputs.isFeatureGateEnabled) {
    return {isAvailable: false, unavailableReason: 'feature-flag-disabled'};
  }

  // Multi-tenant SaaS only; the agent isn't offered on other deployments.
  if (!inputs.isPublicCloud) {
    return {isAvailable: false, unavailableReason: 'not-public-cloud'};
  }

  // Personal entities aren't organizations; the agent is org-scoped only.
  if (inputs.isPersonalAccount) {
    return {isAvailable: false, unavailableReason: 'personal-entity'};
  }

  if (inputs.isLoading || !inputs.orgName) {
    return {isAvailable: false, unavailableReason: 'loading'};
  }

  if (!inputs.areLlmFeaturesEnabled) {
    return {isAvailable: false, unavailableReason: 'org-setting-disabled'};
  }

  return {isAvailable: true};
}

/**
 * useAgentChatOrgQueryArgs resolves the org name and whether to skip the
 * agent's org-scoped Gorilla queries for unsupported users and deployments.
 */
export function useAgentChatOrgQueryArgs(): {
  isFeatureGateEnabled?: boolean;
  isLoggedIn: boolean;
  isPersonalAccount: boolean;
  orgName?: string;
  skip: boolean;
} {
  const isFeatureGateEnabled = useStatsigGateWorkspaceAgent();

  const viewer = useViewer();
  const isLoggedIn = !!viewer;

  const {selectedAccount} = useContext(AccountSelectorContext);
  const isPersonalAccount =
    selectedAccount?.accountType === AccountType.Personal;
  // The agent is multi-tenant SaaS only, so there's no org to target elsewhere.
  const orgName = envIsPublicCloud ? selectedAccount?.name : undefined;
  // NOTE: mirrors computeAvailability's pre-query gates; keep the two in sync.
  const skip =
    !isLoggedIn ||
    !isFeatureGateEnabled ||
    !envIsPublicCloud ||
    isPersonalAccount;
  return {isFeatureGateEnabled, isLoggedIn, isPersonalAccount, orgName, skip};
}

/**
 * useLLMFeaturesEnabled queries organization flags and extracts the current
 * value for the `enable_llm_powered_features` setting.
 */
function useLLMFeaturesEnabled(
  orgName?: string,
  skip = false
): {
  enabled: boolean;
  loading: boolean;
} {
  return useOrganizationFlag('enable_llm_powered_features', orgName, skip);
}

export interface ShareTracesPolicy {
  defaultSharingEnabled: boolean;
  lockedOff: boolean;
  loading: boolean;
}

/**
 * Stable "still resolving" policy — reused as the `useMemo` return value so
 * consumers subscribing via `ShareTracesPolicyContext` don't re-render on
 * every provider render while we're waiting on data.
 */
const SHARE_TRACES_LOADING_POLICY: ShareTracesPolicy = {
  defaultSharingEnabled: false,
  lockedOff: false,
  loading: true,
};

export const ShareTracesPolicyContext = createContext<ShareTracesPolicy>(
  SHARE_TRACES_LOADING_POLICY
);

/**
 * useShareTracesPolicy returns the org's policy around sharing AI traces with
 * W&B. Reads from `ShareTracesPolicyContext` provided by `AgentChatProvider`.
 * Components must be rendered inside that provider.
 */
export function useShareTracesPolicy(): ShareTracesPolicy {
  return useContext(ShareTracesPolicyContext);
}

/**
 * useComputeShareTracesPolicy calculates the org's share-traces policy by
 * combining the stored org flag with any Statsig dynamic-config override.
 *
 * NOTE: This function is for use by the agent chat provider component; external
 * components should use `useShareTracesPolicy` instead.
 */
export function useComputeShareTracesPolicy(): ShareTracesPolicy {
  const {orgName, skip} = useAgentChatOrgQueryArgs();

  const {enabled: defaultSharingEnabled, loading: flagLoading} =
    useShareAITracesEnabled(orgName, skip);
  const {lockedOff, loading: overridesLoading} = useShareAITracesLockedOff(
    orgName,
    skip
  );

  return useMemo(
    () =>
      computeShareTracesPolicy({
        defaultSharingEnabled,
        isLoading: flagLoading || overridesLoading,
        isPublicCloud: envIsPublicCloud,
        lockedOff,
        orgName,
      }),
    [defaultSharingEnabled, flagLoading, lockedOff, orgName, overridesLoading]
  );
}

/** computeShareTracesPolicy resolves the policy from already-fetched inputs. */
export function computeShareTracesPolicy(inputs: {
  defaultSharingEnabled: boolean;
  isLoading: boolean;
  isPublicCloud: boolean;
  lockedOff: boolean;
  orgName?: string;
}): ShareTracesPolicy {
  if (inputs.isLoading || (inputs.isPublicCloud && !inputs.orgName)) {
    return SHARE_TRACES_LOADING_POLICY;
  }
  return {
    defaultSharingEnabled: inputs.defaultSharingEnabled,
    lockedOff: inputs.lockedOff,
    loading: false,
  };
}

/**
 * useShareAITracesEnabled queries organization flags and extracts the current
 * value for the `share_ai_traces_with_wb` setting.
 */
function useShareAITracesEnabled(
  orgName?: string,
  skip = false
): {
  enabled: boolean;
  loading: boolean;
} {
  return useOrganizationFlag('share_ai_traces_with_wb', orgName, skip);
}

/**
 * useProjectMemoryEnabled queries organization flags and extracts the current
 * value for the `aria_project_memory_enabled` setting, which Gorilla composes
 * from the org's own setting and the `aria_project_memory` ramp gate.
 */
export function useProjectMemoryEnabled(
  orgName?: string,
  skip = false
): {
  enabled: boolean;
  loading: boolean;
} {
  return useOrganizationFlag('aria_project_memory_enabled', orgName, skip);
}

/**
 * useShareAITracesLockedOff reads the smart-feature Statsig override and
 * reports whether `share_ai_traces_with_wb` is explicitly locked to `false`.
 * When locked off, admins can't re-enable sharing and individual users
 * can't opt in on a per-turn basis either.
 *
 * Pass `skip: true` to avoid issuing the underlying query entirely (e.g. when
 * the agent feature is disabled and the value won't be used).
 */
function useShareAITracesLockedOff(
  orgName?: string,
  skip = false
): {
  lockedOff: boolean;
  loading: boolean;
} {
  const {config, loading} = useOrganizationDynamicConfigWithLoading(
    SMART_FEATURE_OVERRIDES_CONFIG_ID,
    orgName ?? '',
    skip
  ) as {config?: SmartFeatureOverrides; loading: boolean};

  if (skip || !orgName) {
    return {lockedOff: false, loading: false};
  }

  if (loading && config === undefined) {
    return {lockedOff: false, loading: true};
  }

  const {locked, lockedValue} = isSmartFeatureLocked(
    config,
    'share_ai_traces_with_wb'
  );
  return {lockedOff: locked && lockedValue === false, loading: false};
}

type OrgFlagKey =
  | 'aria_project_memory_enabled'
  | 'enable_llm_powered_features'
  | 'share_ai_traces_with_wb';

/**
 * useOrganizationFlag queries organization flags and extracts the current
 * boolean value for the given flag key, defaulting to `false` if unset.
 */
function useOrganizationFlag(
  flagKey: OrgFlagKey,
  orgName?: string,
  skip = false
): {enabled: boolean; loading: boolean} {
  const {data, loading} = useOrganizationFlagsQuery({
    variables: {orgName: orgName ?? ''},
    skip: skip || !orgName,
    fetchPolicy: 'cache-and-network',
  });

  if (skip || !orgName) {
    return {enabled: false, loading: false};
  }

  if (loading && !data) {
    return {enabled: false, loading: true};
  }

  const flags =
    data?.organization?.flags != null
      ? jsonParseSafe(data.organization.flags)
      : {};

  return {
    enabled: flags?.[flagKey] ?? false,
    loading: false,
  };
}
